import React from "react";
import { View, StyleSheet, TouchableOpacity, ActivityIndicator } from "react-native";
import { ArrowLeftIcon, ArrowRightIcon } from "@phosphor-icons/react";

import type { GetCoursesByDegreeByQueryQuery, GetDegreesByDegreeIdQuery } from "@axio/graphql/generated/graphql";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";

type DegreeByIdResponse = NonNullable<GetDegreesByDegreeIdQuery["getDegree"]>;
type DegreeByIdData = NonNullable<DegreeByIdResponse["data"]>;
type DegreeById = NonNullable<DegreeByIdData[number]>;

type CoursesByDegreeResponse = NonNullable<GetCoursesByDegreeByQueryQuery["getCoursesByDegreeByQuery"]>;
type CoursesByDegreeData = NonNullable<CoursesByDegreeResponse["data"]>;
type CoursesByDegreeCourses = NonNullable<CoursesByDegreeData["courses"]>;
type CoursesByDegreeCourse = NonNullable<CoursesByDegreeCourses[number]>;

interface DegreeDetailProps {
  degreeId: string;
  onBackToDegrees: () => void;
  onCourseSelect: (courseId: string) => void;
  degreesData: DegreeById[];
  coursesByDegreeData: CoursesByDegreeCourse[];
  loading: boolean;
}

const DegreeDetail: React.FC<DegreeDetailProps> = ({
  degreeId,
  onBackToDegrees,
  onCourseSelect,
  degreesData,
  coursesByDegreeData,
  loading,
}) => {
  const degree = degreesData?.find((d) => d.id === degreeId);

  if (loading) {
    return (
      <View style={styles.loadingcontainer}>
        <ActivityIndicator />
        <CustomText text="Loading degree details..." />
      </View>
    );
  }

  if (!degree) {
    return (
      <View style={styles.container}>
        <TouchableOpacity style={styles.backButton} onPress={onBackToDegrees}>
          <View style={styles.backButtonContent}>
            <ArrowLeftIcon size={16} color="#2D2F36" />
            <CustomText text="Back to Degrees" />
          </View>
        </TouchableOpacity>
        <CustomSpacing type="vertical" size="m" />
        <CustomText text="Degree not found" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <TouchableOpacity style={styles.backButton} onPress={onBackToDegrees}>
        <View style={styles.backButtonContent}>
          <ArrowLeftIcon size={20} color="#2D2F36" />
          <CustomText text="Back to Degrees" />
        </View>
      </TouchableOpacity>

      <CustomSpacing type="vertical" size="m" />

      <CustomText text={degree.name} size="xl" weight="bold" useTranslationText={false} />

      {degree.description && (
        <CustomText text={degree.description} useTranslationText={false} style={styles.description} />
      )}

      <CustomSpacing type="vertical" size="l" />

      <CustomText text="Courses in this Degree" size="l" weight="bold" />

      <CustomSpacing type="vertical" size="m" />

      {coursesByDegreeData?.length === 0 ? (
        <CustomText text="No courses found for this degree" />
      ) : (
        <View style={styles.coursesList}>
          {coursesByDegreeData?.map((course) => (
            <TouchableOpacity key={course.id} style={styles.courseCard} onPress={() => onCourseSelect(course.id)}>
              <CustomText text={course.name || "Unnamed Course"} weight="bold" useTranslationText={false} />
              <CustomText text={`Course Code: ${course.course_code || "N/A"}`} size="s" useTranslationText={false} />
              <CustomText text={`Length: ${course.lengthInWeeks || "N/A"} weeks`} size="s" useTranslationText={false} />
              <CustomText
                text={`Enrolled Students: ${course.enrolledStudents?.length || 0}`}
                size="s"
                useTranslationText={false}
              />
              <CustomSpacing type="vertical" size="s" />
              <View style={styles.actionContainer}>
                <CustomText text="Tap to view analytics" size="xs" style={styles.actionText} />
                <ArrowRightIcon size={12} color="#0084FF" />
              </View>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  loadingcontainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    padding: 16,
  },
  container: {
    flex: 1,
    padding: 16,
  },
  backButton: {
    alignSelf: "flex-start",
    padding: 8,
  },
  backButtonContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  description: {
    color: "#666",
    marginTop: 8,
  },
  coursesList: {
    gap: 12,
  },
  courseCard: {
    padding: 16,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e5e5",
  },
  actionContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  actionText: {
    color: "#0084FF",
    fontStyle: "italic",
  },
});

export default DegreeDetail;
